import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Box,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    Snackbar,
    Alert
} from '@mui/material';
import {
    ContentCopy as ContentCopyIcon
} from '@mui/icons-material';

const ContactStatusesDialog = ({ open, onClose, contactStatuses, selectedCampaign }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    // Разделяем контакты по категориям
    const contactsByCategory = useMemo(() => {
        const pending = []; // Ожидающие отправки (waiting)
        const sent = []; // Отправленные (sent, delivered, read)
        const ignored = []; // Игнорированные (skipped или whatsapp_exists === false)

        contactStatuses.forEach(contact => {
            const status = contact.status?.toLowerCase();
            const hasWhatsApp = contact.whatsapp_exists === true;

            // Игнорированные: статус skipped или нет WhatsApp
            if (status === 'skipped' || contact.whatsapp_exists === false) {
                ignored.push(contact);
            }
            // Отправленные: статус sent, delivered или read
            else if (['sent', 'delivered', 'read'].includes(status)) {
                sent.push(contact);
            }
            // Ожидающие: статус waiting или processing
            else if (['waiting', 'processing'].includes(status)) {
                pending.push(contact);
            }
            // Остальные тоже считаем ожидающими
            else {
                pending.push(contact);
            }
        });

        return { pending, sent, ignored };
    }, [contactStatuses]);

    // Получаем контакты для активной вкладки
    const getActiveTabContacts = () => {
        switch (activeTab) {
            case 0:
                return contactsByCategory.pending;
            case 1:
                return contactsByCategory.sent;
            case 2:
                return contactsByCategory.ignored;
            default:
                return [];
        }
    };

    // Копирование номеров активной вкладки
    const handleCopyPhoneNumbers = () => {
        const contacts = getActiveTabContacts();
        const phoneNumbers = contacts
            .map(contact => contact.phone)
            .filter(phone => phone)
            .join('\n');

        if (phoneNumbers) {
            navigator.clipboard.writeText(phoneNumbers).then(() => {
                setSnackbarMessage(`Скопировано ${contacts.length} номеров`);
                setSnackbarOpen(true);
            }).catch(err => {
                console.error('Failed to copy:', err);
                setSnackbarMessage('Ошибка при копировании');
                setSnackbarOpen(true);
            });
        } else {
            setSnackbarMessage('Нет номеров для копирования');
            setSnackbarOpen(true);
        }
    };

    const activeContacts = getActiveTabContacts();

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            Manage Campaign Contacts - {selectedCampaign?.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Copy phone numbers from active tab">
                                <IconButton
                                    size="small"
                                    onClick={handleCopyPhoneNumbers}
                                    disabled={activeContacts.length === 0}
                                >
                                    <ContentCopyIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {contactStatuses.length > 0 ? (
                        <>
                            <Tabs
                                value={activeTab}
                                onChange={(e, newValue) => setActiveTab(newValue)}
                                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                            >
                                <Tab
                                    label={`Pending (${contactsByCategory.pending.length})`}
                                />
                                <Tab
                                    label={`Sent (${contactsByCategory.sent.length})`}
                                />
                                <Tab
                                    label={`Ignored (${contactsByCategory.ignored.length})`}
                                />
                            </Tabs>

                            <Box sx={{ position: 'relative' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Contact</TableCell>
                                            <TableCell>Phone</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Sent At</TableCell>
                                            <TableCell>WhatsApp</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {activeContacts.length > 0 ? (
                                            activeContacts.map((contact, index) => (
                                                <TableRow
                                                    key={contact.campaign_contact_id || contact.contact_id || index}
                                                    sx={{
                                                        backgroundColor: contact.whatsapp_exists === false
                                                            ? 'rgba(244, 67, 54, 0.05)'
                                                            : 'inherit'
                                                    }}
                                                >
                                                    <TableCell>{contact.name || 'Unknown'}</TableCell>
                                                    <TableCell>{contact.phone || '-'}</TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: contact.status === 'sent' ? 'success.main' :
                                                                    contact.status === 'failed' ? 'error.main' :
                                                                    contact.status === 'skipped' ? 'warning.main' :
                                                                    'text.secondary'
                                                            }}
                                                        >
                                                            {contact.status || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {contact.sent_at ?
                                                            new Date(contact.sent_at).toLocaleString() :
                                                            '-'
                                                        }
                                                    </TableCell>
                                                    <TableCell>
                                                        {contact.whatsapp_exists === false ? (
                                                            <Typography variant="body2" color="error">
                                                                No WhatsApp
                                                            </Typography>
                                                        ) : contact.whatsapp_exists === true ? (
                                                            <Typography variant="body2" color="success.main">
                                                                Has WhatsApp
                                                            </Typography>
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">
                                                                Unknown
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    <Typography variant="body2" color="text.secondary">
                                                        No contacts in this category
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="h6" color="text.secondary">
                                No contact statuses available
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Contact statuses will appear here once the campaign is started
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ContactStatusesDialog;